import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Hospital, Lock, Globe, Zap, HeartPulse } from "lucide-react";

const NUERNBERGER_COLOR = "#6B7280";

const NuernbergerTarif = () => {
  const heroFeatures = [
    { icon: Phone, title: "24/7 Hotline", subtitle: "Sofort erreichbar" },
    { icon: Shield, title: "Umfassender Schutz", subtitle: "Alle Risiken abgedeckt" },
    { icon: Globe, title: "Sicherheitstraining", subtitle: "Prävention inklusive" }
  ];

  const coverageAreas = [
    {
      icon: Zap,
      title: "Cyber-Notfallhilfe 24/7",
      items: [
        "IT-Spezialisten 7 Tage die Woche, 24 Stunden erreichbar",
        "Sofortige Hilfe bei laufenden Angriffen",
        "Koordination aller Dienstleister",
        "Forensik & IT-Dienstleistungen",
        "Krisenmanagement und PR-Beratung"
      ]
    },
    {
      icon: Clock,
      title: "Betriebsunterbrechung",
      items: [
        "Ersatz für entgangenen Gewinn",
        "Fortlaufende Kosten während Stillstand",
        "Mehrkosten für Notbetrieb",
        "Umsatzausfall vollständig gedeckt",
        "Keine Wartezeit nach Schadenseintritt"
      ]
    },
    {
      icon: FileText,
      title: "Datenwiederherstellung",
      items: [
        "Professionelle Datenrettung",
        "Wiederherstellung verschlüsselter Daten",
        "Backup-Systeme und Recovery",
        "Kostenübernahme IT-Forensik",
        "Systemwiederaufbau komplett"
      ]
    },
    {
      icon: Lock,
      title: "Cyber-Erpressung & Haftpflicht",
      items: [
        "Spezialistenservice bei Erpressung",
        "Unterstützung bei Ransomware",
        "DSGVO-Bußgelder und Schadensersatz",
        "Ansprüche Dritter bei Datenlecks",
        "Kreditkarten-Vertragsstrafen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Arztpraxen",
      description: "Hausärzte, Fachärzte und Gemeinschaftspraxen mit sensiblen Patientendaten",
      benefits: [
        "24/7-Hotline für schnelle Hilfe im Notfall",
        "Schutz vor Ransomware auf Praxissoftware",
        "DSGVO-Schutz bei Datenweitergabe",
        "Betriebsunterbrechungskosten gedeckt"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 250.000 €"
    },
    {
      icon: HeartPulse,
      title: "Therapiepraxen",
      description: "Physiotherapie, Ergotherapie und andere Heilberufe mit digitalisierten Behandlungsdaten",
      benefits: [
        "Absicherung digitalisierter Patientenakten",
        "Schutz vor Phishing-Attacken",
        "Sicherheitstrainings für Mitarbeiter",
        "Wiederherstellung von Behandlungsdaten"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 100.000 €"
    },
    {
      icon: Users,
      title: "Steuerkanzleien",
      description: "Steuerberater und Wirtschaftsprüfer mit vertraulichen Mandantendaten",
      benefits: [
        "Schutz hochsensibler Finanzdaten",
        "Cyber-Erpressungsschutz",
        "Rechtsberatung bei DSGVO-Verstößen",
        "Krisenmanagement und PR-Unterstützung"
      ],
      recommendation: "Empfohlene Deckung: 100.000 - 500.000 €"
    },
    {
      icon: Building2,
      title: "Kleine Unternehmen",
      description: "Betriebe jeder Größe, die digitale Prozesse nutzen und Kundendaten verarbeiten",
      benefits: [
        "Einfache Beitragsermittlung",
        "Flexible Versicherungssummen",
        "Online-Plattform mit Präventionstools",
        "Keine Wartezeiten bei Vertragsabschluss"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 1.000.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Versicherungssummen",
      description: "Flexibel zwischen 50.000 EUR und 1 Mio. EUR wählbar - abhängig von Ihrer Unternehmensgröße"
    },
    {
      icon: FileText,
      title: "Beitragsermittlung",
      description: "Nur 2 Faktoren entscheidend: Betriebsart und Jahresumsatz für faire Prämien"
    },
    {
      icon: CheckCircle2,
      title: "Keine Wartezeiten",
      description: "Sofortiger Versicherungsschutz und Zugang zur Notfall-Hotline direkt nach Vertragsabschluss"
    },
    {
      icon: Clock,
      title: "Kündigungsfrist",
      description: "3 Monate Kündigungsfrist zum Vertragsende für Planungssicherheit"
    },
    {
      icon: Globe,
      title: "Sicherheitstrainings",
      description: "Optional: Online-Plattform mit E-Learnings, Phishing-Simulationen und Präventionsmaßnahmen"
    },
    {
      icon: Users,
      title: "Mitversicherte Personen",
      description: "Alle Leiter und Mitarbeiter von Niederlassungen in Deutschland automatisch eingeschlossen"
    }
  ];

  const faqs = [
    {
      question: "Wer ist bei der Nürnberger Cyberversicherung mitversichert?",
      answer: "Alle Leiter und Mitarbeiter von Niederlassungen eines Betriebs in Deutschland sind automatisch mitversichert. Es müssen keine einzelnen Personen namentlich benannt werden."
    },
    {
      question: "Wie wird der Beitrag für eine Cyberversicherung ermittelt?",
      answer: "Die Beitragshöhe der Nürnberger Cyberversicherung wird anhand von nur 2 Faktoren ermittelt: der Betriebsart und dem Jahresumsatz. Dies macht die Kalkulation transparent und nachvollziehbar."
    },
    {
      question: "Wie hoch sind die Versicherungssummen?",
      answer: "Die Versicherungssummen hängen von der Unternehmensgröße ab und liegen wahlweise zwischen 50.000 EUR und 1 Mio. EUR. Sie können die für Ihr Unternehmen passende Summe flexibel wählen."
    },
    {
      question: "Was muss man bei der Kündigung der Cyberversicherung beachten?",
      answer: "Die Kündigungsfrist für die Nürnberger Cyberversicherung beträgt in der Regel 3 Monate zum Vertragsende. Dies gibt Ihnen ausreichend Flexibilität bei gleichzeitiger Planungssicherheit."
    },
    {
      question: "Gibt es Wartezeiten bei der Nürnberger Cyberversicherung?",
      answer: "Nein, es gibt bei einer Neuversicherung keine Wartezeiten. Die Notfallhilfe-Hotline sowie alle Leistungen stehen sofort nach dem Abschluss für Schadenfälle bereit."
    },
    {
      question: "Welche Leistungen sind immer inklusive?",
      answer: "Immer inklusive sind: Cyber-Notfallhilfe 24/7, Betriebsunterbrechung, Datenwiederherstellung, Cyber-Erpressung, Cyber-Betrug, Cyber-Haftpflicht und Kreditkarten-Betrug. Optional können Sicherheitstrainings & Prävention hinzugebucht werden."
    },
    {
      question: "Was macht die 24-Stunden-Hotline?",
      answer: "Die Cyber-Notfallhilfe ist 7 Tage die Woche, 24 Stunden erreichbar. IT-Spezialisten helfen bei noch laufenden Hackerangriffen sofort und koordinieren alle notwendigen Maßnahmen zur Schadensbegrenzung."
    },
    {
      question: "Werden auch Vertragsstrafen von Kreditkartenunternehmen übernommen?",
      answer: "Ja, die Nürnberger Cyberversicherung übernimmt auch Vertragsstrafen, die Kreditkarten-Unternehmen bei Datenschutzverletzungen erheben können."
    }
  ];

  const documents = [
    {
      title: "Produktinformationsblatt Cyberversicherung",
      type: "PDF",
      url: "https://www.nuernberger.de/medien/4allportal/ah038_pf.pdf"
    },
    {
      title: "Allgemeine Versicherungsbedingungen",
      type: "PDF",
      url: "https://www.nuernberger.de/medien/4allportal/ah038_avb.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="Nürnberger"
      companyColor={NUERNBERGER_COLOR}
      productRating={{ value: 4.6, count: 298 }}
      priceRange="50-195"
      pageTitle="Nürnberger Cyberversicherung für Arztpraxen - Umfassender Schutz"
      metaDescription="Die Nürnberger Cyberversicherung bietet Arztpraxen umfassenden Schutz mit 24/7-Notfallhilfe, Sicherheitstrainings und flexiblen Versicherungssummen von 50.000 bis 1 Mio. EUR."
      metaKeywords="Nürnberger Cyberversicherung, Cyber-Versicherung Ärzte, Arztpraxis Cyberschutz, Nürnberger Versicherung"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/nuernberger"
      heroLabel="Traditionsversicherer mit moderner IT-Sicherheit"
      heroTitle="Nürnberger Cyberversicherung für Unternehmen"
      heroDescription="Ihr Schutz bei Viren, Trojanern, Hackern & Co. Mit 24-Stunden-Hotline für sofortige Hilfe, Online-Plattform mit Sicherheitstrainings und Phishing-Tests für Ihre Mitarbeiter."
      heroFeatures={heroFeatures}
      alertTitle="Warum die Nürnberger Cyberversicherung?"
      alertDescription="2021 dauerte der Stillstand bei 39 % der betroffenen Betriebe 4 Tage und länger. Bis alles wieder läuft, entgeht Ihnen Gewinn. Die Nürnberger Cyberversicherung schützt Sie zuverlässig vor den finanziellen Folgen eines Cyber-Schadens mit sofortiger 24/7-Hilfe."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default NuernbergerTarif;
